/**
 * 추가폰 변경 이력 관리 컴포넌트
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Collapse,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { AccountSyncHistory, AccountSyncAction } from '../types/accountSync';
import { formatPhoneNumber } from '../utils/phoneNumberUtils';

interface AccountSyncHistoryProps {
  groupId?: string;
  phoneNumber?: string;
  onLoadHistory: (groupId?: string, phoneNumber?: string) => Promise<AccountSyncHistory[]>;
}

interface HistoryRowProps {
  history: AccountSyncHistory;
}

const HistoryRow: React.FC<HistoryRowProps> = ({ history }) => {
  const [open, setOpen] = useState(false);

  const getActionColor = (action: AccountSyncAction) => {
    switch (action) {
      case 'CREATE':
        return 'success';
      case 'ADD':
        return 'primary';
      case 'REMOVE':
        return 'warning';
      case 'DELETE':
        return 'error';
      case 'EXPIRE_SYNC':
        return 'info';
      default:
        return 'default';
    }
  };

  const getActionLabel = (action: AccountSyncAction) => {
    switch (action) {
      case 'CREATE':
        return '그룹 생성';
      case 'ADD':
        return '추가폰 등록';
      case 'REMOVE':
        return '추가폰 제거';
      case 'DELETE':
        return '그룹 삭제';
      case 'EXPIRE_SYNC':
        return '만료일 동기화';
      default:
        return action;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{formatDate(history.executedAt)}</TableCell>
        <TableCell>
          <Chip
            label={getActionLabel(history.action)}
            color={getActionColor(history.action)}
            size="small"
          />
        </TableCell>
        <TableCell>{formatPhoneNumber(history.phoneNumber)}</TableCell>
        <TableCell>{history.executedBy}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                상세 정보
              </Typography>
              <Table size="small" aria-label="details">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">이력 ID</TableCell>
                    <TableCell>{history.id}</TableCell>
                  </TableRow>
                  {history.groupId && (
                    <TableRow>
                      <TableCell component="th" scope="row">그룹 ID</TableCell>
                      <TableCell>{history.groupId}</TableCell>
                    </TableRow>
                  )}
                  {history.reason && (
                    <TableRow>
                      <TableCell component="th" scope="row">사유</TableCell>
                      <TableCell>{history.reason}</TableCell>
                    </TableRow>
                  )}
                  {history.oldValue && (
                    <TableRow>
                      <TableCell component="th" scope="row">이전 값</TableCell>
                      <TableCell>{history.oldValue}</TableCell>
                    </TableRow>
                  )}
                  {history.newValue && (
                    <TableRow>
                      <TableCell component="th" scope="row">새 값</TableCell>
                      <TableCell>{history.newValue}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AccountSyncHistoryComponent: React.FC<AccountSyncHistoryProps> = ({
  groupId,
  phoneNumber,
  onLoadHistory,
}) => {
  const [histories, setHistories] = useState<AccountSyncHistory[]>([]);
  const [filteredHistories, setFilteredHistories] = useState<AccountSyncHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadHistories = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await onLoadHistory(groupId, phoneNumber);
      // 최신 순으로 정렬
      const sortedData = data.sort((a, b) => 
        new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime()
      );
      setHistories(sortedData);
      setFilteredHistories(sortedData);
    } catch (err: any) {
      setError(err.message || '이력을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistories();
  }, [groupId, phoneNumber]);

  useEffect(() => {
    // 검색 필터링
    if (searchTerm) {
      const filtered = histories.filter(history => 
        history.phoneNumber.includes(searchTerm) ||
        history.executedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        history.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getActionLabel(history.action).includes(searchTerm)
      );
      setFilteredHistories(filtered);
    } else {
      setFilteredHistories(histories);
    }
    setPage(0); // 검색 시 첫 페이지로 이동
  }, [searchTerm, histories]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getActionLabel = (action: AccountSyncAction) => {
    switch (action) {
      case 'CREATE':
        return '그룹 생성';
      case 'ADD':
        return '추가폰 등록';
      case 'REMOVE':
        return '추가폰 제거';
      case 'DELETE':
        return '그룹 삭제';
      case 'EXPIRE_SYNC':
        return '만료일 동기화';
      default:
        return action;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          변경 이력
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={loadHistories} size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {filteredHistories.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            변경 이력이 없습니다.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="account sync history table">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>실행 일시</TableCell>
                  <TableCell>작업 유형</TableCell>
                  <TableCell>대상 전화번호</TableCell>
                  <TableCell>실행자</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistories
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((history) => (
                    <HistoryRow key={history.id} history={history} />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredHistories.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="페이지당 행 수:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / 전체 ${count}개`
            }
          />
        </>
      )}
    </Paper>
  );
};

export default AccountSyncHistoryComponent;